// src/components/TeamHeaderCard.jsx
// ESPN-style team header — shows team name, record, last matchup, manager name
import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import { ref, onValue } from "firebase/database";
import { TeamLogo } from "./TeamLogo";

export function TeamHeaderCard({ leagueCode, onTabChange }) {
  const uid = auth.currentUser?.uid;
  const [room,   setRoom]   = useState(null);
  const [league, setLeague] = useState(null);

  useEffect(() => {
    if (!leagueCode) return;
    const code = leagueCode.toUpperCase();
    const u1 = onValue(ref(db, `rooms/${code}`),   s => setRoom(s.val() || null));
    const u2 = onValue(ref(db, `leagues/${code}`), s => setLeague(s.val() || null));
    return () => { u1(); u2(); };
  }, [leagueCode]);

  const mySlot = useMemo(() => {
    if (!uid || !room?.slots) return null;
    const entry = Object.entries(room.slots).find(([, v]) => v?.claimedBy === uid);
    return entry ? entry[0] : null;
  }, [uid, room?.slots]);

  const teamCustom = mySlot ? (room?.teamCustom?.[mySlot] || {}) : {};
  const displayName = teamCustom.name || room?.slots?.[mySlot]?.name || mySlot || "My Team";
  const color  = teamCustom.color  || "var(--gold)";
  const emoji  = teamCustom.emoji  || "🏏";
  const imgUrl = teamCustom.imgUrl || "";

  // W/L record from standings — only count completed weeks
  const record = useMemo(() => {
    if (!mySlot || !league?.season?.schedule) return null;
    const currentWeekNum = Number(league?.season?.week || 1);
    let w = 0, l = 0, t = 0;
    Object.keys(league.season.schedule).map(Number).forEach(wk => {
      if (wk >= currentWeekNum) return; // skip current and future weeks
      (league.season.schedule[String(wk)]?.matchups || []).forEach(m => {
        const hp = Number(m.homePoints), ap = Number(m.awayPoints);
        if (!Number.isFinite(hp) || !Number.isFinite(ap)) return;
        if (m.home === mySlot || m.away === mySlot) {
          const mine = m.home === mySlot ? hp : ap;
          const theirs = m.home === mySlot ? ap : hp;
          if (mine > theirs) w++;
          else if (theirs > mine) l++;
          else t++;
        }
      });
    });
    return { w, l, t };
  }, [mySlot, league?.season?.schedule, league?.season?.week]);

  // Last matchup
  const lastMatchup = useMemo(() => {
    if (!mySlot || !league?.season?.schedule) return null;
    const week = league?.season?.week || 1;
    const prevWeek = week - 1;
    if (prevWeek < 1) return null;
    const matchups = league.season.schedule[String(prevWeek)]?.matchups || [];
    const m = matchups.find(x => x.home === mySlot || x.away === mySlot);
    if (!m || m.homePoints === null || m.awayPoints === null) return null;
    const myPts = m.home === mySlot ? m.homePoints : m.awayPoints;
    const oppPts = m.home === mySlot ? m.awayPoints : m.homePoints;
    const opp = m.home === mySlot ? m.away : m.home;
    const oppCustom = room?.teamCustom?.[opp] || {};
    return {
      opp: oppCustom.name || opp,
      myPts, oppPts,
      won: myPts > oppPts,
    };
  }, [mySlot, league, room]);

  const currentWeek = league?.season?.week || 1;
  const managerName = room?.slots?.[mySlot]?.name || auth.currentUser?.displayName || "Manager";

  if (!mySlot) return null;

  return (
    <div className="team-header-card">
      {/* Team logo */}
      <TeamLogo imgUrl={imgUrl} emoji={emoji} name={displayName} color={color} size={56} />

      {/* Team info */}
      <div className="team-header-info">
        <div className="team-header-name" style={{ color }}>
          {displayName}
        </div>
        <div className="team-header-meta">
          {managerName} · Week {currentWeek}
          {record && (
            <span style={{ marginLeft: 10, color: "var(--text)" }}>
              {record.w}–{record.l}{record.t > 0 ? `–${record.t}` : ""}
            </span>
          )}
        </div>
      </div>

      {/* Last matchup */}
      {lastMatchup && (
        <div className="team-header-matchup">
          <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 3, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            Last Matchup
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: lastMatchup.won ? "var(--green)" : "var(--red)" }}>
            {lastMatchup.won ? "W" : "L"} {lastMatchup.myPts}–{lastMatchup.oppPts}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-dim)" }}>vs {lastMatchup.opp}</div>
        </div>
      )}

      {/* Record card */}
      {record && (
        <div className="team-header-record">
          <div style={{ fontSize: 24, fontWeight: 900, color }}>
            {record.w}–{record.l}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>RECORD</div>
        </div>
      )}

      {/* Quick actions */}
      <div className="team-header-actions">
        <button className="mini" onClick={() => onTabChange?.("waiver")} type="button">
          + Add Player
        </button>
        <button className="mini" onClick={() => onTabChange?.("trades")} type="button">
          Trades
        </button>
      </div>
    </div>
  );
}
